import React, { ChangeEvent, Fragment, FunctionComponent } from "react";

import { Caption, CheckBoxComponentValue, ColSpan, Divider, GridColumnCount, GridGapSize, GridLayout, GridLayoutItem, Layout, SpacingSize, Tab, Tabs } from "@multiweb/reactComponents";
import { LOC_ADDRESS_TYPE, LOC_ADDRESSEE, LOC_AREA_OF_COMPETENCE, LOC_ATTRIBUTE, LOC_ATTRIBUTE_NAME, LOC_ATTRIBUTE_VALUE, LOC_BASIC_CONDITION, LOC_CLASS, LOC_COMMENT, LOC_CONDITION_PARAMETERS, LOC_CONDTION_SELECTION_DOCUMENTS, LOC_CURRENT_PARTICIPANTS, LOC_CURRENT_USER, LOC_DATA_CONDITION, LOC_DELAYED_SENDING, LOC_DOCUMENT_CLASS, LOC_ENTERED_DIRECTLY, LOC_FOLLOWING_PARTICIPANTS, LOC_FROM_OTHER_RECORDS, LOC_MESSAGE_FORMAT, LOC_MESSAGE_SUBJECT, LOC_OWNER, LOC_REPORT_TEMPLATE, LOC_ROLE, LOC_SUMMARY_EMAIL, LOC_TEXT } from "../../../../../../locKeys";
import { parseStringToBoolean } from "../../../../../../utils";
import { MessageFormat, SendMessageRecipientType } from "../../../../../../workflowModel/Actions";
import { CONST_ACTION_COMMENT_AT, CONST_ACTION_CONDITION_AT, CONST_SEND_MAIL_ATTR_NAME_AT, CONST_SEND_MAIL_CLASS_NAME_AT, CONST_SEND_MAIL_DATA_CONDITION_AT, CONST_SEND_MAIL_DOCUMENT_ATTRIBUTE_AT, CONST_SEND_MAIL_DOCUMENT_CLASS_NAME_AT, CONST_SEND_MAIL_DOCUMENT_CONDITION_AT, CONST_SEND_MAIL_DOCUMENT_CONDITION_PARAMS_AT, CONST_SEND_MAIL_FORMAT_AT, CONST_SEND_MAIL_RECIPIENT_AT, CONST_SEND_MAIL_RECIPIENT_TYPE_AT, CONST_SEND_MAIL_REPORT_TEMPLATE_AT, CONST_SEND_MAIL_SEND_POSTPONE_AT, CONST_SEND_MAIL_SUBJECT_AT, CONST_SEND_MAIL_SUMMARY_MAIL_AT, CONST_SEND_MAIL_TEXT_AT } from "../../../../../../workflowParser/constants";
import { Edit, Memo, WfCheckBox, WfOption, WfSelect } from "../../../../components";
import { MAX_INPUT_LENGTH_L, MAX_INPUT_LENGTH_XXL } from "../constants";
import { ActionPageProps } from "../types";

export type SendMailPageComponentProps = {
    readonly messageText: string;
    readonly attachmentsText: string;
} & ActionPageProps;

export const SendMailPageComponent: FunctionComponent<SendMailPageComponentProps> = ({
    properties,
    hostKey,
    updateAction,
    messageText,
    attachmentsText,
}) => {
    const recipientType = properties[CONST_SEND_MAIL_RECIPIENT_TYPE_AT];
    const isRecipientConditionType = recipientType === SendMessageRecipientType.condition;
    const isRecipientShown = recipientType !== SendMessageRecipientType.actualUser
        && recipientType !== SendMessageRecipientType.owner
        && recipientType !== SendMessageRecipientType.participants
        && recipientType !== SendMessageRecipientType.nextParticipants
        && recipientType !== SendMessageRecipientType.condition;

    const onRecipientTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_RECIPIENT_AT, value: "" }, { key: CONST_SEND_MAIL_RECIPIENT_TYPE_AT, value: e.target.value }]);
    };

    const onFormatTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_FORMAT_AT, value: e.target.value }]);
    };

    const onRecipientChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_RECIPIENT_AT, value: e.target.value }]);
    };

    const onClassNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_CLASS_NAME_AT, value: e.target.value }]);
    };

    const onAttrNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_ATTR_NAME_AT, value: e.target.value }]);
    };

    const onDataConditionChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_DATA_CONDITION_AT, value: e.target.value }]);
    };

    const onConditionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateAction([{ key: CONST_ACTION_CONDITION_AT, value: e.target.value }]);
    };

    const onCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateAction([{ key: CONST_ACTION_COMMENT_AT, value: e.target.value }]);
    };

    const onSummarySendChange = (value: CheckBoxComponentValue) => {
        updateAction([{ key: CONST_SEND_MAIL_SUMMARY_MAIL_AT, value: value ? "true" : "false" }]);
    };

    const onSendPostponeChange = (value: CheckBoxComponentValue) => {
        updateAction([{ key: CONST_SEND_MAIL_SEND_POSTPONE_AT, value: value ? "true" : "false" }]);
    };

    const onSubjectChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_SUBJECT_AT, value: e.target.value }]);
    };

    const onTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_TEXT_AT, value: e.target.value }]);
    };

    const onReportTemplateChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_REPORT_TEMPLATE_AT, value: e.target.value }]);
    };

    const onDocumentClassNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_DOCUMENT_CLASS_NAME_AT, value: e.target.value }]);
    };

    const onDocumentAttributeChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_DOCUMENT_ATTRIBUTE_AT, value: e.target.value }]);
    };

    const onDocumentConditionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_DOCUMENT_CONDITION_AT, value: e.target.value }]);
    };

    const onDocumentConditionParamsChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_MAIL_DOCUMENT_CONDITION_PARAMS_AT, value: e.target.value }]);
    };

    return (
        <GridLayout columns={GridColumnCount.two} gap={GridGapSize.two}>
            <GridLayoutItem colSpan={ColSpan.one}>
                <Caption locKey={LOC_ADDRESS_TYPE} />
                <WfSelect qa={LOC_ADDRESS_TYPE} value={properties[CONST_SEND_MAIL_RECIPIENT_TYPE_AT]} onChange={onRecipientTypeChange} {...{ hostKey }}>
                    <WfOption qa={SendMessageRecipientType.value} value={SendMessageRecipientType.value} locKey={LOC_ENTERED_DIRECTLY} />
                    <WfOption qa={SendMessageRecipientType.attributeValue} value={SendMessageRecipientType.attributeValue} locKey={LOC_ATTRIBUTE_VALUE} />
                    <WfOption qa={SendMessageRecipientType.actualUser} value={SendMessageRecipientType.actualUser} locKey={LOC_CURRENT_USER} />
                    <WfOption qa={SendMessageRecipientType.owner} value={SendMessageRecipientType.owner} locKey={LOC_OWNER} />
                    <WfOption qa={SendMessageRecipientType.participants} value={SendMessageRecipientType.participants} locKey={LOC_CURRENT_PARTICIPANTS} />
                    <WfOption qa={SendMessageRecipientType.nextParticipants} value={SendMessageRecipientType.nextParticipants} locKey={LOC_FOLLOWING_PARTICIPANTS} />
                    <WfOption qa={SendMessageRecipientType.role} value={SendMessageRecipientType.role} locKey={LOC_ROLE} />
                    <WfOption qa={SendMessageRecipientType.okruh} value={SendMessageRecipientType.okruh} locKey={LOC_AREA_OF_COMPETENCE} />
                    <WfOption qa={SendMessageRecipientType.condition} value={SendMessageRecipientType.condition} locKey={LOC_FROM_OTHER_RECORDS} />
                </WfSelect>
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.one}>
                <Caption locKey={LOC_MESSAGE_FORMAT} />
                <WfSelect qa={LOC_MESSAGE_FORMAT} value={properties[CONST_SEND_MAIL_FORMAT_AT]} onChange={onFormatTypeChange} {...{ hostKey }}>
                    <option value={MessageFormat.text}>text</option>
                    <option value={MessageFormat.HTML}>HTML</option>
                </WfSelect>
            </GridLayoutItem>
            {isRecipientShown &&
                <GridLayoutItem colSpan={ColSpan.two}>
                    <Caption required locKey={LOC_ADDRESSEE} />
                    <Edit qa={LOC_ADDRESSEE} required maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_SEND_MAIL_RECIPIENT_AT]} onChange={onRecipientChange} {...{ hostKey }} />
                </GridLayoutItem>
            }
            {isRecipientConditionType &&
                <Fragment>
                    <GridLayoutItem colSpan={ColSpan.two}>
                        <Caption required locKey={LOC_CLASS} />
                        <Edit qa={LOC_CLASS} required maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_SEND_MAIL_CLASS_NAME_AT]} onChange={onClassNameChange} {...{ hostKey }} />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={ColSpan.two}>
                        <Caption required locKey={LOC_ATTRIBUTE_NAME} />
                        <Edit qa={LOC_ATTRIBUTE_NAME} required maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_SEND_MAIL_ATTR_NAME_AT]} onChange={onAttrNameChange} {...{ hostKey }} />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={ColSpan.two}>
                        <Caption locKey={LOC_DATA_CONDITION} />
                        <Edit qa={LOC_DATA_CONDITION} maxLength={MAX_INPUT_LENGTH_XXL} value={properties[CONST_SEND_MAIL_DATA_CONDITION_AT]} onChange={onDataConditionChange} {...{ hostKey }} />
                    </GridLayoutItem>
                </Fragment>
            }
            <GridLayoutItem colSpan={ColSpan.one}>
                <WfCheckBox qa={LOC_SUMMARY_EMAIL} value={parseStringToBoolean(properties[CONST_SEND_MAIL_SUMMARY_MAIL_AT], false)} textLocKey={LOC_SUMMARY_EMAIL} showFalseAsCrossed onChange={onSummarySendChange} {...{ hostKey }} />
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.one}>
                <WfCheckBox qa={LOC_DELAYED_SENDING} value={parseStringToBoolean(properties[CONST_SEND_MAIL_SEND_POSTPONE_AT], false)} textLocKey={LOC_DELAYED_SENDING} showFalseAsCrossed onChange={onSendPostponeChange} {...{ hostKey }} />
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.two}>
                <Tabs>
                    <Tab tabName={messageText}>
                        <Layout spacingSizeTop={SpacingSize.l}>
                            <GridLayout columns={GridColumnCount.one} gap={GridGapSize.two}>
                                <GridLayoutItem>
                                    <Caption locKey={LOC_MESSAGE_SUBJECT} />
                                    <Edit qa={LOC_MESSAGE_SUBJECT} maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_SEND_MAIL_SUBJECT_AT]} onChange={onSubjectChange} {...{ hostKey }} />
                                </GridLayoutItem>
                                <GridLayoutItem>
                                    <Caption required locKey={LOC_TEXT} />
                                    <Memo qa={LOC_TEXT} required rows={12} value={properties[CONST_SEND_MAIL_TEXT_AT]} onChange={onTextChange} {...{ hostKey }} />
                                </GridLayoutItem>
                            </GridLayout>
                        </Layout>
                    </Tab>
                    <Tab tabName={attachmentsText}>
                        <Layout spacingSizeTop={SpacingSize.l}>
                            <GridLayout columns={GridColumnCount.two} gap={GridGapSize.two}>
                                <GridLayoutItem colSpan={ColSpan.two}>
                                    <Caption locKey={LOC_REPORT_TEMPLATE} />
                                    <Edit qa={LOC_REPORT_TEMPLATE} maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_SEND_MAIL_REPORT_TEMPLATE_AT]} onChange={onReportTemplateChange} {...{ hostKey }} />
                                </GridLayoutItem>
                                <GridLayoutItem colSpan={ColSpan.one}>
                                    <Caption locKey={LOC_DOCUMENT_CLASS} />
                                    <Edit qa={LOC_DOCUMENT_CLASS} maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_SEND_MAIL_DOCUMENT_CLASS_NAME_AT]} onChange={onDocumentClassNameChange} {...{ hostKey }} />
                                </GridLayoutItem>
                                <GridLayoutItem colSpan={ColSpan.one}>
                                    <Caption locKey={LOC_ATTRIBUTE} />
                                    <Edit qa={LOC_ATTRIBUTE} maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_SEND_MAIL_DOCUMENT_ATTRIBUTE_AT]} onChange={onDocumentAttributeChange} {...{ hostKey }} />
                                </GridLayoutItem>
                                <GridLayoutItem colSpan={ColSpan.two}>
                                    <Caption locKey={LOC_CONDTION_SELECTION_DOCUMENTS} />
                                    <Memo qa={LOC_CONDTION_SELECTION_DOCUMENTS} rows={3} value={properties[CONST_SEND_MAIL_DOCUMENT_CONDITION_AT]} onChange={onDocumentConditionChange} {...{ hostKey }} />
                                </GridLayoutItem>
                                <GridLayoutItem colSpan={ColSpan.two}>
                                    <Caption locKey={LOC_CONDITION_PARAMETERS} />
                                    <Edit qa={LOC_CONDITION_PARAMETERS} maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_SEND_MAIL_DOCUMENT_CONDITION_PARAMS_AT]} onChange={onDocumentConditionParamsChange} {...{ hostKey }} />
                                </GridLayoutItem>
                            </GridLayout>
                        </Layout>
                    </Tab>
                </Tabs>
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.two} height={1}>
                <Divider />
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.two}>
                <Caption locKey={LOC_BASIC_CONDITION} />
                <Memo qa={LOC_BASIC_CONDITION} value={properties[CONST_ACTION_CONDITION_AT]} onChange={onConditionChange} {...{ hostKey }} />
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.two}>
                <Caption locKey={LOC_COMMENT} />
                <Memo qa={LOC_COMMENT} value={properties[CONST_ACTION_COMMENT_AT]} onChange={onCommentChange} {...{ hostKey }} />
            </GridLayoutItem>
        </GridLayout>
    );
};
